import { useMemo, useRef, useState } from "react";
import JoditEditor from "jodit-react";
import type { MenuProps } from "antd";
import { Dropdown } from "antd";
import "jodit/es2021/jodit.min.css";
import type { InsertMode } from "jodit/types/types";

import "./styles.scss";

type MentionOption = {
    value: string;
    label?: React.ReactNode;
};

type JoditWithMentionsProps = {
    value: string;
    onChange?: (html: string) => void;
    options?: MentionOption[];
};

export default function JoditWithMentions({
    value,
    onChange,
    options = [],
}: JoditWithMentionsProps) {
    const editorRef = useRef<any>(null);
    const joditInstanceRef = useRef<any>(null);

    const [mentionOpen, setMentionOpen] = useState(false);
    const [mentionKeyword, setMentionKeyword] = useState("");
    const [dropdownPos, setDropdownPos] = useState({
        left: 0,
        top: 0,
    });

    const filteredOptions = useMemo(() => {
        const keyword = mentionKeyword.toLowerCase();

        return options.filter((item) => {
            const label =
                typeof item.label === "string" || typeof item.label === "number"
                    ? String(item.label).toLowerCase()
                    : "";

            const value = String(item.value ?? "").toLowerCase();

            return label.includes(keyword) || value.includes(keyword);
        });
    }, [options, mentionKeyword]);

    const getEditorRoot = (): HTMLElement | null => {
        return joditInstanceRef.current?.editor ?? null;
    };

    const updateDropdownPosition = () => {
        const selection = window.getSelection();

        if (!selection || selection.rangeCount === 0) return;

        const range = selection.getRangeAt(0).cloneRange();
        range.collapse(false);

        let rect = range.getBoundingClientRect();

        if (!rect || (rect.left === 0 && rect.top === 0)) {
            const marker = document.createElement("span");
            marker.textContent = "\u200b";

            range.insertNode(marker);
            rect = marker.getBoundingClientRect();

            marker.parentNode?.removeChild(marker);
        }

        setDropdownPos({
            left: rect.left,
            top: rect.bottom + 6,
        });
    };

    const getTextBeforeCaret = () => {
        const editorRoot = getEditorRoot();
        const selection = window.getSelection();

        if (!editorRoot || !selection || selection.rangeCount === 0) return "";

        const range = selection.getRangeAt(0);

        if (!editorRoot.contains(range.endContainer)) {
            return "";
        }

        const clonedRange = range.cloneRange();
        clonedRange.selectNodeContents(editorRoot);
        clonedRange.setEnd(range.endContainer, range.endOffset);

        return clonedRange.toString();
    };

    const detectMention = () => {
        const instance = joditInstanceRef.current;

        // Nếu đang ở source mode thì không bật mention
        if (instance?.getRealMode && instance.getRealMode() !== 1) {
            setMentionOpen(false);
            setMentionKeyword("");
            return;
        }

        const textBeforeCaret = getTextBeforeCaret();

        const match = textBeforeCaret.match(/(?:^|\s)@([\wÀ-ỹ.-]*)$/u);

        if (match) {
            setMentionKeyword(match[1] || "");
            updateDropdownPosition();
            setMentionOpen(true);
        } else {
            setMentionOpen(false);
            setMentionKeyword("");
        }
    };

    const replaceCurrentMention = (item: MentionOption) => {
        const editorRoot = getEditorRoot();
        const selection = window.getSelection();

        if (!editorRoot || !selection || selection.rangeCount === 0) return;

        const range = selection.getRangeAt(0);

        if (!editorRoot.contains(range.endContainer)) return;

        const textBeforeCaret = getTextBeforeCaret();
        const match = textBeforeCaret.match(/@([\wÀ-ỹ.-]*)$/u);

        if (!match) return;

        const mentionLength = match[0].length;

        /**
         * Cách này chỉ an toàn khi @keyword nằm trong cùng text node.
         * Với case gõ bình thường trong editor thì thường đúng.
         */
        if (range.endContainer.nodeType === Node.TEXT_NODE) {
            const textNode = range.endContainer as Text;
            const startOffset = Math.max(0, range.endOffset - mentionLength);

            range.setStart(textNode, startOffset);
            range.deleteContents();
        }

        const span = document.createElement("span");
        span.className = "mention-token";
        span.setAttribute("data-key", item.value);
        span.setAttribute(
            "style",
            "display:inline-block;background:#eef2ff;color:#1d4ed8;border-radius:4px;padding:1px 4px;font-size:13px;"
        );
        span.textContent = `{{${item.value}}}`;

        const space = document.createTextNode(" ");

        range.insertNode(space);
        range.insertNode(span);

        range.setStartAfter(space);
        range.setEndAfter(space);

        selection.removeAllRanges();
        selection.addRange(range);

        setMentionOpen(false);
        setMentionKeyword("");

        const html = joditInstanceRef.current?.value ?? "";
        onChange?.(html);
    };

    const menuItems: MenuProps["items"] = filteredOptions.map((item) => ({
        key: item.value,
        label: (
            <div
                onMouseDown={(e) => {
                    e.preventDefault();
                    replaceCurrentMention(item);
                }}
            >
                {item.label ?? item.value}
            </div>
        ),
    }));
    const cleanEmptyHtml = (value?: string) => {
        const html = (value || "").trim();

        const emptyValues = [
            "<p><br></p>",
            "<p></p>",
            "<br>",
            "<p>&nbsp;</p>",
        ];

        return emptyValues.includes(html) ? "" : value || "";
    };
    const config = useMemo(
        () => ({
            readonly: false,
            iframe: false,
            height: 500,
            toolbarAdaptive: false,
            defaultMode: 1,

            askBeforePasteHTML: false,
            askBeforePasteFromWord: false,
            placeholder: "",
            //     // Khi paste HTML thì tự insert như HTML, không hỏi modal
            defaultActionOnPaste: "insert_as_html" as InsertMode,
            buttons: [
                "source",
                "fullsize",
                // "preview",
                // "print",
                "|",

                "bold",
                "italic",
                "underline",
                "strikethrough",
                "superscript",
                "subscript",
                "|",

                "ul",
                "ol",
                "outdent",
                "indent",
                "|",

                "font",
                "fontsize",
                "brush",
                "paragraph",
                "|",

                "align",
                "lineHeight",
                "|",

                "table",
                "link",
                "image",
                "|",

                "selectall",
                "|",

                "undo",
                "redo",
                "|",

                "find",
                // "spellcheck",
                "|",

            ],
            events: {
                afterInit: (editor: any) => {
                    joditInstanceRef.current = editor;

                    if (cleanEmptyHtml(editor.value) === "") {
                        editor.value = "";
                    }
                },

                change: (newValue: string) => {
                    const cleanValue = cleanEmptyHtml(newValue);

                    if (cleanValue !== newValue) {
                        joditInstanceRef.current.value = "";
                    }

                    onChange?.(cleanValue);
                },
                keyup: () => {
                    detectMention();
                },
                click: () => {
                    detectMention();
                },
                blur: () => {
                    setTimeout(() => {
                        setMentionOpen(false);
                    }, 150);
                },
            },
            uploader: {
                insertImageAsBase64URI: true,
            },
        }),
        []
    );

    return (
        <div style={{ position: "relative" }} className="custom-dropdown-mention">
            <JoditEditor
                ref={editorRef}
                value={value}
                config={config}
                onBlur={(newContent: string) => {
                    onChange?.(newContent);
                }}
                onChange={() => { }}
            />

            <Dropdown
                open={mentionOpen && Boolean(menuItems?.length)}
                menu={{ items: menuItems }}
                trigger={[]}
                placement="bottomLeft"
            >
                <div
                    className=""
                    style={{
                        position: "fixed",
                        left: dropdownPos.left,
                        top: dropdownPos.top,
                        width: 1,
                        height: 1,
                        zIndex: 9999,
                    }}
                />
            </Dropdown>
        </div>
    );
}